// V14.8 — Reusable rich Create/Edit dialog for SOCIAL POSTS.
// Used by MPSocialPostsPage AND MPExerciseDashboard so the same Author Profile
// + Engagement + Hashtags + Schedule + Media + Live Preview layout appears
// everywhere a social post is created in MP.
import React, { useState, useEffect, useRef } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { TIMEZONES } from '../../lib/timezone';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { SocialPostTemplate } from './SocialPostTemplates';
import { postsApi, mediaApi } from '../../lib/mpApi';
import { toast } from 'sonner';
import { Loader2, FileUp, X, Film, CalendarClock, Eye, Hash } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (url) => url?.startsWith('/') ? `${BACKEND_URL}${url}` : url;

const emptyForm = () => ({
  exercise_id: '', platform: '', content: '', status: 'draft',
  author: { name: '', handle: '', bio: '', followers: 0, is_verified: false },
  engagement: { likes: 0, comments: 0, shares: 0, retweets: 0, views: 0 },
  hashtags: [], verification_status: 'none', location: '', media_urls: [],
  schedule: null,
});

const SocialPostFormDialog = ({
  open, onOpenChange, editingPost = null, exercises = [], platforms = [],
  lockedExerciseId = null, onSaved,
}) => {
  const [formData, setFormData] = useState(emptyForm());
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileInputRef = useRef(null);

  // Hydrate form ONLY when the dialog transitions open or the edited post changes.
  // We intentionally exclude `exercises` from deps — the parent (MPExerciseDashboard)
  // polls every 10s and recreates `[exercise]` on every render, which would otherwise
  // reset the form mid-typing (V14.8 bug fix).
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!open) return;
    if (editingPost) {
      setFormData({
        exercise_id: editingPost.exercise_id, platform: editingPost.platform,
        content: editingPost.content, status: editingPost.status,
        author: editingPost.author || emptyForm().author,
        engagement: editingPost.engagement || emptyForm().engagement,
        hashtags: editingPost.hashtags || [],
        verification_status: editingPost.verification_status || 'none',
        location: editingPost.location || '',
        media_urls: editingPost.media_urls || [],
        schedule: editingPost.schedule || null,
      });
    } else {
      setFormData({
        ...emptyForm(),
        exercise_id: lockedExerciseId || exercises[0]?.exercise_id || '',
      });
    }
    setSelectedFiles([]);
  }, [open, editingPost?.post_id, lockedExerciseId]);

  // Filter platforms by (a) post type — Social dialog excludes news platforms;
  // (b) the selected exercise's allow-list when configured.
  const selectedExObj = exercises.find(e => e.exercise_id === formData.exercise_id);
  const socialCategories = ['social', 'video', 'messaging', 'custom', 'regional'];
  const typeFiltered = platforms.filter(p => socialCategories.includes(p.category) || !p.category);
  const formPlatforms = selectedExObj?.selected_platforms?.length > 0
    ? typeFiltered.filter(p => selectedExObj.selected_platforms.includes(p.platform_id) || selectedExObj.selected_platforms.includes(p.name))
    : typeFiltered;

  const handleFileSelect = (e) => {
    setSelectedFiles(prev => [...prev, ...Array.from(e.target.files)]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async () => {
    if (!formData.exercise_id || !formData.platform || !formData.content) {
      toast.error('Exercise, platform and content are required'); return;
    }
    setSaving(true);
    try {
      let mediaUrls = [...formData.media_urls];
      for (const file of selectedFiles) {
        try {
          const r = await mediaApi.upload(file);
          mediaUrls.push(r.file_url);
        } catch { toast.error(`Failed to upload ${file.name}`); }
      }
      const payload = { ...formData, media_urls: mediaUrls, post_type: 'social_media' };
      if (editingPost) {
        await postsApi.update(editingPost.post_id, payload);
        toast.success('Post updated');
      } else {
        await postsApi.create(payload);
        toast.success('Post created');
      }
      onOpenChange(false);
      if (onSaved) onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const livePreview = {
    ...formData,
    post_id: 'preview',
    post_type: 'social_media',
    timestamp: new Date().toISOString(),
    media_urls: [
      ...formData.media_urls,
      ...selectedFiles.filter(f => f.type.startsWith('image/')).map(f => URL.createObjectURL(f)),
    ],
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="mp-social-post-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading uppercase text-foreground">
            {editingPost ? 'Edit Social Post' : 'Create Social Post'}
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 py-4">
          {/* Form */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Exercise</Label>
                <Select value={formData.exercise_id} onValueChange={v => setFormData({ ...formData, exercise_id: v })} disabled={!!lockedExerciseId}>
                  <SelectTrigger className="bg-muted border-input"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{exercises.map(e => <SelectItem key={e.exercise_id} value={e.exercise_id}>{e.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Platform</Label>
                <Select value={formData.platform} onValueChange={v => setFormData({ ...formData, platform: v })}>
                  <SelectTrigger className="bg-muted border-input"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{formPlatforms.map(p => <SelectItem key={p.platform_id} value={p.name}>{p.display_name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Content</Label>
              <Textarea value={formData.content} onChange={e => setFormData({ ...formData, content: e.target.value })}
                placeholder="What's happening?" rows={4}
                className="bg-muted border-input placeholder:text-muted-foreground"
                data-testid="mp-social-content-input" />
            </div>

            {/* Author */}
            <div className="bg-muted/50 rounded-lg p-3 border border-input space-y-3">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Author Profile</Label>
              <div className="grid grid-cols-2 gap-3">
                <Input placeholder="Display Name" value={formData.author.name}
                  onChange={e => setFormData({ ...formData, author: { ...formData.author, name: e.target.value } })}
                  className="bg-muted border-input h-8 text-sm" />
                <Input placeholder="@handle" value={formData.author.handle}
                  onChange={e => setFormData({ ...formData, author: { ...formData.author, handle: e.target.value } })}
                  className="bg-muted border-input h-8 text-sm" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Input type="number" placeholder="Followers" value={formData.author.followers}
                  onChange={e => setFormData({ ...formData, author: { ...formData.author, followers: parseInt(e.target.value) || 0 } })}
                  className="bg-muted border-input h-8 text-sm" />
                <div className="flex items-center gap-2">
                  <Switch checked={formData.author.is_verified}
                    onCheckedChange={v => setFormData({ ...formData, author: { ...formData.author, is_verified: v }, verification_status: v ? 'verified' : 'none' })} />
                  <Label className="text-xs text-muted-foreground">Verified</Label>
                </div>
              </div>
            </div>

            {/* Engagement */}
            <div className="bg-muted/50 rounded-lg p-3 border border-input space-y-3">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Engagement</Label>
              <div className="grid grid-cols-3 gap-2">
                {[
                  { key: 'likes', label: 'Likes' },
                  { key: 'comments', label: 'Comments' },
                  { key: 'shares', label: 'Shares' },
                  { key: 'retweets', label: 'Retweets' },
                  { key: 'views', label: 'Views' },
                ].map(field => (
                  <Input key={field.key} type="number" placeholder={field.label} value={formData.engagement[field.key]}
                    onChange={e => setFormData({ ...formData, engagement: { ...formData.engagement, [field.key]: parseInt(e.target.value) || 0 } })}
                    className="bg-muted border-input h-8 text-sm" />
                ))}
              </div>
            </div>

            {/* Hashtags */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
                <Hash className="w-3 h-3" /> Hashtags
              </Label>
              <Input value={formData.hashtags.join(', ')}
                onChange={e => setFormData({ ...formData, hashtags: e.target.value.split(',').map(h => h.trim()).filter(Boolean) })}
                placeholder="tag1, tag2" className="bg-muted border-input" />
            </div>

            {/* Schedule */}
            <div className="bg-muted/50 rounded-lg p-3 border border-input space-y-3">
              <div className="flex items-center justify-between">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1.5">
                  <CalendarClock className="w-3.5 h-3.5" /> Schedule
                </Label>
                <Switch checked={formData.status === 'scheduled'}
                  onCheckedChange={v => setFormData({
                    ...formData, status: v ? 'scheduled' : 'draft',
                    schedule: v ? { scheduled: true, publish_datetime: '', timezone: 'UTC' } : null,
                  })}
                  data-testid="mp-social-schedule-toggle" />
              </div>
              {formData.status === 'scheduled' && (
                <div className="grid grid-cols-2 gap-3">
                  <Input type="datetime-local" value={formData.schedule?.publish_datetime || ''}
                    onChange={e => setFormData({ ...formData, schedule: { ...formData.schedule, scheduled: true, publish_datetime: e.target.value } })}
                    className="bg-muted border-input h-8 text-sm"
                    data-testid="mp-social-schedule-datetime" />
                  <Select value={formData.schedule?.timezone || 'UTC'}
                    onValueChange={v => setFormData({ ...formData, schedule: { ...formData.schedule, timezone: v } })}>
                    <SelectTrigger className="bg-muted border-input h-8 text-sm"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map(tz => (
                        <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            {/* Media */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Media</Label>
              <div className="border-2 border-dashed border-input rounded-lg p-4 text-center bg-muted/30">
                <input ref={fileInputRef} type="file" multiple accept="image/*,video/*" onChange={handleFileSelect} className="hidden" />
                <Button type="button" variant="outline" onClick={() => fileInputRef.current?.click()}
                  className="border-input text-muted-foreground hover:bg-muted" data-testid="mp-social-upload-btn">
                  <FileUp className="w-4 h-4 mr-2" />Add Media
                </Button>
              </div>
              {(selectedFiles.length > 0 || formData.media_urls.length > 0) && (
                <div className="flex flex-wrap gap-2">
                  {formData.media_urls.map((url, i) => (
                    <div key={`url-${i}`} className="relative group/img w-16 h-16 border border-input rounded-lg overflow-hidden">
                      <img src={resolveUrl(url)} alt="" className="w-full h-full object-cover" />
                      <button onClick={() => setFormData({ ...formData, media_urls: formData.media_urls.filter((_, idx) => idx !== i) })}
                        className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-foreground rounded-full flex items-center justify-center opacity-0 group-hover/img:opacity-100"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                  {selectedFiles.map((file, i) => (
                    <div key={`file-${i}`} className="relative group/img w-16 h-16 border border-input rounded-lg overflow-hidden bg-muted flex items-center justify-center">
                      {file.type.startsWith('video/') ? <Film className="w-6 h-6 text-muted-foreground" /> : <img src={URL.createObjectURL(file)} alt="" className="w-full h-full object-cover" />}
                      <button onClick={() => setSelectedFiles(prev => prev.filter((_, idx) => idx !== i))}
                        className="absolute top-0 right-0 w-4 h-4 bg-red-500 text-foreground rounded-full flex items-center justify-center opacity-0 group-hover/img:opacity-100"><X className="w-3 h-3" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Live Preview */}
          <div className="space-y-3">
            <div className="flex items-center gap-2 mb-2">
              <Eye className="w-4 h-4 text-muted-foreground" />
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Live Preview</Label>
            </div>
            <div className="bg-muted/30 rounded-lg border border-input p-4 max-h-[60vh] overflow-y-auto">
              {formData.platform ? (
                <SocialPostTemplate post={livePreview} platform={formData.platform} />
              ) : (
                <div className="text-center py-12 text-muted-foreground">
                  <p className="text-sm">Select a platform to see preview</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="border-t border-border pt-4">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="border-input text-muted-foreground hover:bg-muted">Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-red-600 hover:bg-red-700 text-foreground" data-testid="mp-social-save-btn">
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null} Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default SocialPostFormDialog;
